import { classifyApiError, isApiError } from './api-error.js';
/** `visitor.flow.manage`, `tenant.owner`, `visitor.visit.self-check-out`. */
const CODE_NAME = /^[a-z][a-z0-9_-]*(\.[a-z][a-z0-9_-]*)+$/;
const MAX_NAME_LENGTH = 100;
/**
 * An identifier, not part of the route: a cuid2 (every Prisma id, `@default(cuid(2))`), a
 * UUID, or a number. Route words are short or hyphenated, so neither shape collides.
 */
const ID_SEGMENT = /^([a-z0-9]{20,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;
const METHOD = /^[A-Z]{3,7}$/;
/** One line per error object, however many seams it passes through (SDK observer + cache). */
const reported = new WeakSet();
function safeName(value) {
    if (value === undefined || value.length > MAX_NAME_LENGTH)
        return undefined;
    return CODE_NAME.test(value) ? value : undefined;
}
/** `/api/web/flows/<cuid>/screens/7?x=1` -> `/api/web/flows/:id/screens/:id`. */
export function toPathTemplate(path) {
    const withoutQuery = path.split(/[?#]/, 1)[0] ?? '';
    return withoutQuery
        .split('/')
        .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
        .join('/');
}
/**
 * The console line for a permission denial, or `null` when `error` is not one. Pure — the
 * formatting is tested without touching `console`.
 *
 * `[InstaNexus] Permission denied: PATCH /api/web/flows/:id requires visitor.flow.manage (role: visitor.frontdesk, request a1b2c3)`
 */
export function formatPermissionDenied(error, context = {}) {
    if (!isApiError(error) || classifyApiError(error) !== 'forbidden')
        return null;
    const method = error.request !== undefined && METHOD.test(error.request.method) ? error.request.method : undefined;
    const what = method !== undefined && error.request !== undefined
        ? `: ${method} ${toPathTemplate(error.request.path)}`
        : '';
    const permission = safeName(error.permission) ?? safeName(context.permission);
    const requires = permission === undefined ? '' : ` requires ${permission}`;
    const roles = (context.roles ?? []).map(safeName).filter((role) => role !== undefined);
    const role = roles.length === 0 ? '' : `role: ${roles.join(', ')}, `;
    // `requestId` is opaque and server-minted (or the transport's `client-*` fallback).
    return `[InstaNexus] Permission denied${what}${requires} (${role}request ${error.requestId})`;
}
/**
 * Writes the one `console.warn` line for a permission denial. Returns `true` when `error`
 * WAS a denial (whether or not this call printed it — a second report of the same error
 * object is deduplicated), so a caller can skip its own generic failure UI.
 */
export function reportPermissionDenied(error, context) {
    const line = formatPermissionDenied(error, context);
    if (line === null)
        return false;
    if (typeof error === 'object' && error !== null) {
        if (reported.has(error))
            return true;
        reported.add(error);
    }
    // eslint-disable-next-line no-console -- The ONE sanctioned console call in browser code: a permission denial must be visible in the console as a denial, not as our bug (user request 2026-10-07; docs/adr/0034 amendment, point 3).
    console.warn(line);
    return true;
}
/**
 * The general seam. A permission denial is reported via `reportPermissionDenied`; every
 * other failure is intentionally a no-op today (see the module doc).
 */
export function reportClientError(report) {
    if (reportPermissionDenied(report.cause, { roles: report.roles, permission: report.permission })) {
        return;
    }
    void report;
}
//# sourceMappingURL=report-error.js.map